import { Card, CardContent, Typography, Chip, Box } from '@mui/material';
import { Handle, Position, type NodeProps } from 'reactflow';
import type { OrganogramNode } from '../types';

interface OrganogramNodeData {
  node: OrganogramNode;
  onNodeClick: (nodeId: string) => void;
  isRootNode?: boolean; // True if this user has no manager
  currentUserId?: string; // ID of the currently logged-in user
}

export default function OrganogramNodeCard({ data }: NodeProps<OrganogramNodeData>) {
  const { node, onNodeClick, isRootNode = false, currentUserId } = data;
  const fullName = `${node.firstName} ${node.lastName}`;
  const isCurrentUser = currentUserId && node.id === currentUserId;

  return (
    <>
      {/* Top handle for parent connection */}
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
      
      <Card
        sx={{
          minWidth: 180,
          maxWidth: 220,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
          border: '2px solid',
          borderColor: isCurrentUser 
            ? 'primary.main' 
            : isRootNode 
            ? 'warning.main' 
            : 'divider',
          backgroundColor: isCurrentUser
            ? 'rgba(25, 118, 210, 0.08)'
            : isRootNode 
            ? 'rgba(255, 152, 0, 0.08)' 
            : 'background.paper',
          boxShadow: isCurrentUser ? 3 : 1,
        }}
        onClick={() => onNodeClick(node.id)}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              textAlign: 'center',
              wordBreak: 'break-word',
            }}
          >
            {fullName}
          </Typography>
          
          {node.roleTemplateName && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
              <Chip
                label={node.roleTemplateName}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
          )}
          
          {node.departmentName && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                textAlign: 'center',
                fontSize: '0.7rem',
              }}
            >
              {node.departmentName}
            </Typography>
          )}
        </CardContent>
      </Card>
      
      {/* Bottom handle for children connections */}
      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
    </>
  );
}
